/**
 * WhatsAppSendButton
 * A button that fires a WhatsApp report and shows a beautiful
 * success/error toast notification. Only visible to allowed users.
 *
 * Props:
 *   user        - the logged-in user object { username, ... }
 *   label       - button label e.g. "Send to WhatsApp"
 *   apiUrl      - full URL to POST to (with query params already appended)
 *   disabled    - whether the button should be disabled (e.g. no date selected)
 */

import React, { useState } from 'react';
import {
  Button, Snackbar, Alert, CircularProgress, Box, Typography
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

// Users allowed to fire WhatsApp reports
const ALLOWED_USERS = ['udhim', 'shashi'];

const WhatsAppSendButton = ({ user, label = 'Send to WhatsApp', apiUrl, payload = {}, disabled = false }) => {
  const [loading, setLoading]   = useState(false);
  const [toast, setToast]       = useState({ open: false, type: 'success', message: '' });

  // Only render for allowed users
  if (!user || !ALLOWED_USERS.includes(user.username?.toLowerCase())) {
    return null;
  }

  const handleSend = async () => {
    if (!apiUrl || disabled) return;
    setLoading(true);
    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        // Count successful recipients
        const results = data.results || data.imageResult?.results || data.recipients || [];
        const successCount = results.filter(r => r.success).length;
        const total = results.length;
        setToast({
          open: true,
          type: 'success',
          message: successCount === total
            ? `✅ Sent to all ${total} recipients successfully!`
            : `⚠️ Sent to ${successCount}/${total} recipients. Check logs.`,
        });
      } else {
        setToast({ open: true, type: 'error', message: `❌ Failed: ${data.error || data.message || 'Unknown error'}` });
      }
    } catch (err) {
      setToast({ open: true, type: 'error', message: `❌ Network error: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Button
        variant="contained"
        size="large"
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <WhatsAppIcon />}
        onClick={handleSend}
        disabled={disabled || loading}
        sx={{
          backgroundColor: '#25D366',
          color: '#fff',
          fontWeight: 700,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: { xs: '0.85rem', md: '0.95rem' },
          px: { xs: 2, md: 3 },
          '&:hover': { backgroundColor: '#1ebe5d' },
          '&:disabled': { backgroundColor: '#a5d6a7', color: '#fff' },
          minWidth: { xs: '100%', md: 'auto' },
        }}
      >
        {loading ? 'Sending...' : label}
      </Button>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 16, md: 24 } }}
      >
        <Alert
          onClose={() => setToast(t => ({ ...t, open: false }))}
          severity={toast.type}
          variant="filled"
          icon={toast.type === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
          sx={{
            width: '100%',
            maxWidth: { xs: '95vw', md: 480 },
            borderRadius: 3,
            fontSize: { xs: '0.85rem', md: '0.9rem' },
            fontWeight: 600,
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            '& .MuiAlert-message': { display: 'flex', alignItems: 'center' },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 'inherit' }}>
            {toast.message}
          </Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppSendButton;
